import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, from, fromEvent, merge, Observable, of, Subscription} from 'rxjs';
import {StompConnectionStatus} from './stomp-connection-status.enum';
import {ProtocolCheck} from '../main/desktop/protocol-check';
import {ClientStatus, ContingencyModeChangedEvent, R2CloudClientStatus} from '../models';
import {delay, distinctUntilKeyChanged, filter, map, startWith, switchMap, take, takeUntil, throttleTime} from 'rxjs/operators';
import {USER_TOPIC} from '../../../main';
import {ComponentCleaner} from '../component-cleaner';
import {R2CloudStompService} from './r2-cloud-stomp.service';
import {DomainPathService} from '../domain-path/domain-path.service';
import {AuthService} from '../auth.service';
import {DomainPath} from '../domain-path/models-domain';
import {isWindowsOS} from '../helpers/kluh';
import {LocalClientService} from '../main/login/local-client.service';
import {ContingencyService} from '../main/login/contingency.service';

@Injectable({
    providedIn: 'root'
})
export class R2CloudClientService extends ComponentCleaner {
    private static timeoutMs = 3000;
    private numberOfAttemptsToOpenWindowsClient = 0;
    private clientListenerSubscription!: Subscription;
    private connectedOnce = false;
    private status: ClientStatus = {clientId: null, webClientId: null, status: R2CloudClientStatus.STOMP_NOT_CONNECTED};
    private readonly _$startClient: Observable<ClientStatus>;
    private readonly enabledSubject = new BehaviorSubject<boolean>(true);
    private readonly clientStatusSubject = new BehaviorSubject<ClientStatus>(this.status);
    private readonly manualClick = new BehaviorSubject<Event>({isTrusted: true} as Event);
    private customProtocolMaxAttempts = 30;
    private customProtocolCurrentAttempts = 0;
    private readonly amountOfAttempts: number = 4;
    private isClientOpenNow = false;

    constructor(private stomp: R2CloudStompService,
                private authService: AuthService,
                private domainPathService: DomainPathService,
                private localClientService: LocalClientService,
                private contingencyService: ContingencyService,
    ) {
        super();
        this._$startClient = this.initStartClientObservable();

        this.addSubscription(this.localClientService.isClientOpen$()
            .subscribe(v => this.isClientOpenNow = !!v));

        this.addSubscription(
            this.stomp.stompTopic<ContingencyModeChangedEvent>(`${USER_TOPIC}/contingency-mode-changed`)
                .subscribe((evt) => this.contingencyService.applyServerEvent(evt))
        );
    }

    private static startProtocolCheck(token: string, clientId: String, domainPath: DomainPath): Observable<ClientStatus> {
        if (isWindowsOS()) {
            const promise = ProtocolCheck
                .protocolCheckPromise(
                    `KluhR2CloudClient://${token},${clientId},${domainPath.secure},${domainPath.apiDomain},${domainPath.staticDomain}`,
                    R2CloudClientService.timeoutMs
                );
            return from(promise).pipe(
                take(1),
                map((status) => ({
                        status: status,
                        webClientId: null,
                        clientId: null
                    } as ClientStatus))
            );
        } else {
            return of({status: R2CloudClientStatus.OPENING, clientId: null, webClientId: null} as ClientStatus);
        }
    }

    startClientListener(): void {
        if (!this.clientListenerSubscription) {
            this.clientListenerSubscription = this._$startClient.pipe(takeUntil(this.onDestroy$)).subscribe((status) => {
                this.status = status;
                this.clientStatusSubject.next(status);
            });
            this.addSubscription(this.clientListenerSubscription);
        }
        this.addSubscription(this.onDestroy$.subscribe(() => {
            this.clientStatusSubject.complete();
            this.enabledSubject.complete();
        }));
    }

    clientConnectionChanges(): Observable<ClientStatus> {
        return this.clientStatusSubject.asObservable();
    }

    private initStartClientObservable(): Observable<ClientStatus> {
        return combineLatest([
            this.enabledSubject,
            this.stomp.connectionStatus$.pipe(startWith(StompConnectionStatus.DISCONNECTED)),
            this.stomp.stompTopic<ClientStatus>(`${USER_TOPIC}/client-status`)
                .pipe(startWith({clientId: null, webClientId: null, status: R2CloudClientStatus.STOMP_NOT_CONNECTED} as ClientStatus)),
            this.domainPathService.domainPath$,
            this.authService.getFirebaseToken(),
            this.userActionsObservable()
        ]).pipe(switchMap((combined: [boolean, StompConnectionStatus, ClientStatus, DomainPath, string, Event]) => {
                const enabled = combined[0];
                if (!enabled) {
                    return of({clientId: null, webClientId: null, status: R2CloudClientStatus.NOT_CONNECTED} as ClientStatus);
                }
                const stompConnectionStatus: StompConnectionStatus = combined[1];
                if (stompConnectionStatus !== StompConnectionStatus.OK) {
                    return of({clientId: null, webClientId: null, status: R2CloudClientStatus.STOMP_NOT_CONNECTED} as ClientStatus);
                }
                const clientStatus: ClientStatus = combined[2];
                const event = combined[5];
                const clientId: string = this.stomp.webClientId;
                const domainPath: DomainPath = combined[3];
                const firebaseToken: string = combined[4];
                if (clientStatus.status === R2CloudClientStatus.NOT_CONNECTED) {
                    this.numberOfAttemptsToOpenWindowsClient = 0;
                }
                switch (clientStatus.status) {
                    case R2CloudClientStatus.CONNECTED:
                        if (this.numberOfAttemptsToOpenWindowsClient < 1) {
                            this.numberOfAttemptsToOpenWindowsClient++;
                            this.tryToConnectFewTimes(event, clientStatus, firebaseToken, clientId, domainPath);
                            return of(clientStatus);
                        }
                        break;
                    case R2CloudClientStatus.CLIENT_UPDATE:
                        this.connectedOnce = true;
                        break;
                    case R2CloudClientStatus.STOMP_NOT_CONNECTED:
                        return of(clientStatus);
                    case R2CloudClientStatus.OPENING:
                    case R2CloudClientStatus.INSTALLED:
                    case R2CloudClientStatus.NOT_CONNECTED:
                    case R2CloudClientStatus.NOT_INSTALLED:
                        return this.startClient(event, clientStatus, firebaseToken, clientId, domainPath);
                }
                return of(clientStatus);
            }),
            distinctUntilKeyChanged('status'));
    }

    initStartCustomProtocolObservable(customProtocol: string): void {
        this.addSubscription(this.userActionsObservable().subscribe(() => {
            if (this.customProtocolMaxAttempts >= this.customProtocolCurrentAttempts) {
                this.tryToOpenCustomProtocolFewTimes(customProtocol, 4);
            }
        }));
    }

    private userActionsObservable(): Observable<Event> {
        return merge(
            merge(
                fromEvent(document, 'click'),
                fromEvent(document, 'contextmenu'),
                fromEvent(document, 'dblclick'),
                fromEvent(document, 'mousedown'),
                fromEvent(document, 'mouseenter'),
                fromEvent(document, 'mouseleave'),
                fromEvent(document, 'mousemove'),
                fromEvent(document, 'mouseout'),
                fromEvent(document, 'mouseover'),
                fromEvent(document, 'mouseup'),
            ).pipe(throttleTime(R2CloudClientService.timeoutMs), filter(() => this.connectedOnce)),
            merge(
                fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden)),
                this.manualClick
            )
        );
    }

    private tryToConnectFewTimes(event: Event, clientStatus: ClientStatus, firebaseToken: string, clientId: String, domainPath: DomainPath): void {
        this.tryStartClient(event, clientStatus, firebaseToken, clientId, domainPath);
    }

    private tryStartClient(event: Event, clientStatus: ClientStatus, firebaseToken: string, clientId: String, domainPath: DomainPath): void {
        this.startClient(event, clientStatus, firebaseToken, clientId, domainPath);
        for (let i = 1; i < this.amountOfAttempts; i++) {
            const msDelay = (4000 * i);
            this.startClientWithDelay(event, clientStatus, firebaseToken, clientId, domainPath, msDelay);
        }
    }

    private startClientWithDelay(event: Event, clientStatus: ClientStatus, firebaseToken: string, clientId: String, domainPath: DomainPath, msDelay: number): void {
        setTimeout(() => {
            this.startClient(event, clientStatus, firebaseToken, clientId, domainPath);
        }, msDelay);
    }

    private startClient(event: Event, clientStatus: ClientStatus, firebaseToken: string, clientId: String, domainPath: DomainPath): Observable<ClientStatus> {
        if (!event.isTrusted || document.hidden) {
            return of(clientStatus);
        }

        if (this.isClientOpenNow) {
            if (this.contingencyService.isEnabled()) {
                if (this.isClientConnected()) {
                    return this.localClientService.exit$({reason: 'user-click'}).pipe(
                        switchMap(() => {
                            this.localClientService.start();
                            return of({
                                status: R2CloudClientStatus.OPENING,
                                clientId: null,
                                webClientId: null
                            } as ClientStatus);
                        }),
                        switchMap(() => {
                            this.localClientService.start();
                            return of({
                                status: R2CloudClientStatus.OPENING,
                                clientId: null,
                                webClientId: null
                            } as ClientStatus);
                        })
                    );
                } else {
                    return this.clientConnectionChanges();
                }
            } else {
                if (!this.isClientConnected()) {
                    return this.localClientService.exit$({reason: 'user-click'}).pipe(
                        switchMap(() => R2CloudClientService.startProtocolCheck(firebaseToken, clientId, domainPath).pipe(
                                startWith({
                                    status: R2CloudClientStatus.OPENING,
                                    clientId: null,
                                    webClientId: null
                                } as ClientStatus),
                                delay(5000) // atraso aqui
                            )),
                        // Se der erro no exit$, ainda assim continua depois de 5s
                        switchMap(() => R2CloudClientService.startProtocolCheck(firebaseToken, clientId, domainPath).pipe(
                                startWith({
                                    status: R2CloudClientStatus.OPENING,
                                    clientId: null,
                                    webClientId: null
                                } as ClientStatus)
                            ))
                    );
                }
                return of(clientStatus);
            }
        } else {
            if (this.contingencyService.isEnabled()) {
                this.localClientService.start();
                return of({
                    status: R2CloudClientStatus.OPENING,
                    clientId: null,
                    webClientId: null
                } as ClientStatus);
            } else {
                return R2CloudClientService.startProtocolCheck(firebaseToken, clientId, domainPath).pipe(
                    startWith({
                        status: R2CloudClientStatus.OPENING,
                        clientId: null,
                        webClientId: null
                    } as ClientStatus),
                    delay(5000)
                );
            }
        }
    }

    private openGenericCustomProtocol(customProtocol: string): void {
        if (isWindowsOS()) {
            this.startClientClick({isTrusted: true} as Event);
            ProtocolCheck.protocolCheckPromise(customProtocol, 3000).then(() => {
            });
            this.customProtocolCurrentAttempts++;
        }
    }

    private openGenericCustomProtocolWithDelay(customProtocol: string, msDelay: number): void {
        setTimeout(() => {
            this.openGenericCustomProtocol(customProtocol);
        }, msDelay);
    }

    private tryToOpenCustomProtocolFewTimes(customProtocol: string, count: number): void {
        for (let i = 1; i < count; i++) {
            const msDelay = ((4000 * i) + this.customProtocolCurrentAttempts * 1500);
            this.openGenericCustomProtocolWithDelay(customProtocol, msDelay);
        }
    }

    isClientConnected(): boolean {
        return this.status.status === R2CloudClientStatus.CONNECTED;
    }

    disable(): void {
        this.enabledSubject.next(false);
    }

    enable(): void {
        this.enabledSubject.next(true);
    }

    enableChanges(): Observable<boolean> {
        return this.enabledSubject.asObservable();
    }

    startClientClick(e: Event): void {
        this.connectedOnce = true;
        this.manualClick.next(e);
    }
}
